/**
 * IM DMP 確認画面（/format/rirekisho_job_change/edit/confirm）専用
 * 職歴テーブルから所属企業・入社年月を抽出するユーティリティ。
 *
 * IM DMP タグ側の同意判定・発火制御は ad-consent.js / ad-networks.js が担う。
 * 本ファイルは Vue / DOM に依存しない純関数のみ。
 */

interface EmploymentRecord {
  company_name: string
  join_date: string
  join_year: number
  join_month: number
}

export interface ImDmpConfirmPayload {
  company_name: string
  join_date: string
  join_year: number
  join_month: number
  employment_count: number
  employments: EmploymentRecord[]
}

function cleanText(value: unknown): string {
  return String(value ?? '')
    .replace(/[\u00a0\u3000]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * 確認画面の職歴テーブル（tr.experience 以降の行）から雇用データを抽出する。
 * PC用・モバイル用の重複 tbody は seen キーで除外する。
 * 取得できない場合（対象行なし）は null を返す。
 */
export function extractConfirmPageEmployments(
  root: Document = document
): ImDmpConfirmPayload | null {
  const bodies = root.querySelectorAll('tbody')
  const employments: EmploymentRecord[] = []
  const seen: Record<string, boolean> = {}

  bodies.forEach((tbody) => {
    const experienceRow = tbody.querySelector('tr.experience')
    if (!experienceRow) return

    let row = experienceRow.nextElementSibling
    while (row) {
      if (row.classList.contains('empty')) break

      const cells = row.children
      if (cells.length >= 3) {
        const yearText = cleanText(cells[0].textContent)
        const monthText = cleanText(cells[1].textContent)
        const companyName = cleanText(cells[2].textContent)

        const yearMatch = yearText.match(/(?:19|20)\d{2}/)
        const monthMatch = monthText.match(/\d{1,2}/)

        if (yearMatch && monthMatch && companyName) {
          const year = Number(yearMatch[0])
          const month = Number(monthMatch[0])

          if (month >= 1 && month <= 12) {
            const joinDate = `${year}-${String(month).padStart(2, '0')}`
            const key = `${companyName}|${joinDate}`

            if (!seen[key]) {
              seen[key] = true
              employments.push({
                company_name: companyName,
                join_date: joinDate,
                join_year: year,
                join_month: month
              })
            }
          }
        }
      }
      row = row.nextElementSibling
    }
  })

  if (!employments.length) return null

  const latest = employments[employments.length - 1]
  return {
    company_name: latest.company_name,
    join_date: latest.join_date,
    join_year: latest.join_year,
    join_month: latest.join_month,
    employment_count: employments.length,
    employments
  }
}
